// 📄 hooks/useGenericSolutionProgress.ts
// Progreso de los canales de integración genéricos (SLP/Sourcing/Contracts)
// para la pestaña de solución del ProjectStatsPanel — equivalente a
// useAllChannelsProgress.ts pero para solucionIntegrationService.ts en vez
// de commerce/integrationService.ts.
//
// Diferencia clave con Commerce: acá el checklist se namespacea por canal
// (`{solucion}Integration.environments.DEV.checklistProgress.{canalSlug}.{stepId}`)
// en vez de un mapa plano — porque varios canales de una misma solución
// reutilizan los mismos ids de paso genéricos (parametros/mapeo/prueba, ver
// integrationChannels.ts). Una sola lectura de Firestore para todos los
// canales de la solución (getCanalIntegracionConfig), igual que Commerce.
import { useEffect, useState } from 'react';
import { getCanalIntegracionConfig } from '../services/solucionIntegrationService.ts';
import { getCanalesDeSolucion, SolucionConCanales } from '../pages/Proyectos/integrationChannels.ts';
import { ChannelProgress } from './useAllChannelsProgress.ts';

const stripStepNumber = (title: string): string => title.replace(/^\d+\.\s*/, '');

function getContextText(completed: number, total: number, lastCompleted: string | null): string {
  if (total === 0) return 'Sin pasos definidos';
  if (completed === 0) return 'Sin configurar';
  if (completed >= total) return 'Completado';
  return `${lastCompleted} completado`;
}

export function useGenericSolutionProgress(projectId: string, solucion: SolucionConCanales | null) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, ChannelProgress>>({});

  useEffect(() => {
    if (!projectId || !solucion) {
      setLoading(false);
      return;
    }

    let cancelado = false;

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const config = await getCanalIntegracionConfig(projectId, solucion);
        if (cancelado) return;

        const checklistProgress: Record<string, Record<string, boolean>> = config?.environments?.DEV?.checklistProgress || {};
        const canales = getCanalesDeSolucion(solucion);

        const result: Record<string, ChannelProgress> = {};
        canales.forEach((canal) => {
          const progresoCanal = checklistProgress[canal.slug] || {};
          const completedSteps = canal.steps.filter((s) => progresoCanal[s.id] === true);
          const total = canal.steps.length;
          const completed = completedSteps.length;
          const lastCompleted = completedSteps.length > 0
            ? stripStepNumber(completedSteps[completedSteps.length - 1].title)
            : null;

          result[canal.slug] = { completed, total, lastCompleted, contextText: getContextText(completed, total, lastCompleted) };
        });

        setProgress(result);
      } catch (err) {
        console.error(`Error cargando progreso de ${solucion}:`, err);
        if (!cancelado) setError('No se pudo cargar el progreso de los canales.');
      } finally {
        if (!cancelado) setLoading(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [projectId, solucion]);

  return { loading, error, progress };
}
